<template>
  <div class="login-container">
    <img src="../../../assets/images/login_bg.jpg" alt="" style='width:100%;' class="image">
    <el-form
      ref="loginFormRef"
      :model="loginForm"
      :rules="loginRules"
      class="login-form"
      autocomplete="on"
      label-position="left"
    >
      <div class="title-container">
        <div>
          <img src="../../../assets/images/logo.png" alt="" style='width:100%;'>
        </div>
        <h3 class="title">
          {{ t("login.title") }}
        </h3>
      </div>

      <el-form-item prop="username" required>
        <span class="svg-container">
          <i class="iconfont icon-yonghu" />
        </span>
        <el-input
          ref="userNameRef"
          v-model="loginForm.username"
          :placeholder="t('login.username')"
          type="text"
          tabindex="1"
          clearable
          autocomplete="on"
        />
      </el-form-item>
      <el-form-item prop="password" required>
        <span class="svg-container">
          <i class="iconfont icon-mima" />
        </span>
        <el-input
          ref="passwordRef"
          v-model="loginForm.password"
          :placeholder="t('login.password')"
          type="password"
          tabindex="2"
          clearable
          autocomplete="on"
        />
      </el-form-item>
      <div class="login-form-Code">
        <el-form-item prop="verifyCode" required>
          <span class="svg-container">
            <i class="iconfont icon-yanzhengma" />
          </span>
          <el-input
            ref="verifiCodeRef"
            v-model="loginForm.verifyCode"
            :placeholder="t('login.verifyCode')"
            tabindex="2"
            clearable
            autocomplete="off"
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        <img :src="loginForm.verifiCodeImg" alt="" class="verifi-code-img" @click="getVerifiCode">
      </div>

      <el-checkbox v-model="loginForm.rememberMe" label="自动登录" style=""></el-checkbox>
      <el-button
        type="primary"
        style="width:100%;"
        @click.prevent="handleLogin"
      >
        {{ t("login.logIn") }}
      </el-button>
      <p @click="openActivationDialog" style="text-align:center;color:#fff;margin-top:15px;" class="cursor">新用户登陆/忘记密码</p>
    </el-form>
    <ActivationDialog :visible="activationDialogVisible" @update:visible="(val: boolean) => activationDialogVisible = val" @close="() => activationDialogVisible = false" />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  reactive,
  watch,
  ref,
  toRefs
} from 'vue'
import { useRoute, LocationQuery, useRouter } from 'vue-router'
import { useStore } from '@/store'
import { UserActionTypes } from '@/store/modules/user/action-types'
import { useI18n } from 'vue-i18n'
import { getVerifiCode } from '@/apis/user'
import ActivationDialog from './components/ActivationDialog.vue'
import { resolveLoginRedirectTarget } from './redirect'
document.title = '登陆'
export default defineComponent({
  components: {
    ActivationDialog
  },
  setup() {
    const userNameRef = ref(null)
    const passwordRef = ref(null)
    const loginFormRef = ref(null)
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const { t } = useI18n()
    const state = reactive({
      loginForm: {
        username: '',
        password: '',
        verifyCode: null,
        rememberMe: false,
        id: null,
        verifiCodeImg: null
      },
      loginRules: {
        username: [{ required: true, message: '请输入正确用户名', trigger: ['change', 'blur'] }],
        password: [{ required: true, message: '请输入正确密码', trigger: ['change', 'blur'] }],
        verifyCode: [{ required: true, message: '请输入正确验证码', trigger: ['change', 'blur'] }]
      },
      loading: false,
      showDialog: false,
      redirect: '',
      otherQuery: {},
      activationDialogVisible: false
    })

    const methods = reactive({
      navigateAfterLogin: () => {
        const targetPath = resolveLoginRedirectTarget(state.redirect, '/')
        router.push({
          path: targetPath,
          query: state.otherQuery
        })
      },
      handleLogin: () => {
        (loginFormRef.value as any).validate(async(valid: boolean) => {
          if (valid) {
            state.loading = true
            try {
              const res = await store.dispatch(UserActionTypes.ACTION_LOGIN, state.loginForm)
              if (res) {
                methods.navigateAfterLogin()
                setTimeout(() => {
                  state.loading = false
                }, 0.5 * 1000)
              }
            } catch (error:any) {
              state.loading = false
              state.loginForm.verifyCode = null
              methods.getVerifiCode()
            }
          } else {
            return false
          }
        })
      },
      getVerifiCode: () => {
        getVerifiCode().then((res: any) => {
          if (res) {
            state.loginForm.verifiCodeImg = res.image
            state.loginForm.id = res.id
          }
        }).catch(() => {
          console.log('getVerifiCode')
        })
      },
      openActivationDialog: () => {
        state.activationDialogVisible = true
      }
    })

    function getOtherQuery(query: LocationQuery) {
      return Object.keys(query).reduce((acc, cur) => {
        if (cur !== 'redirect') {
          acc[cur] = query[cur]
        }
        return acc
      }, {} as LocationQuery)
    }

    watch(() => route.query, query => {
      if (query) {
        state.redirect = query.redirect?.toString() ?? ''
        state.otherQuery = getOtherQuery(query)
      }
    }, { immediate: true })

    onMounted(() => {
      methods.getVerifiCode()
      if (state.loginForm.username === '') {
        (userNameRef.value as any).focus()
      } else if (state.loginForm.password === '') {
        (passwordRef.value as any).focus()
      }
    })

    return {
      userNameRef,
      passwordRef,
      loginFormRef,
      ...toRefs(state),
      ...toRefs(methods),
      t
    }
  }
})
</script>

<style lang="scss">
// References: https://www.zhangxinxu.com/wordpress/2018/01/css-caret-color-first-line/
@supports (-webkit-mask: none) and (not (cater-color: $loginCursorColor)) {
  .login-container .el-input {
    input {
      color: $loginCursorColor;
    }
    input::first-line {
      color: $lightGray;
    }
  }
}

.login-container {
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;

  .el-input {
    display: inline-block;
    height: 47px;
    width: 85%;
    margin-bottom: 6px;
    .el-input__wrapper,.el-input.is-focus .el-input__wrapper{
      width: 100%;
      border-radius:0;
      border: none;
      box-shadow: 0 0 0 0!important;
      background: transparent;
    }
    input {
      font-size: 16px;
      height: 47px;
      background: transparent;
      border: 0px;
      border-radius: 0px;
      padding: 12px 5px 12px 15px;
      color: $lightGray;
      caret-color: $loginCursorColor;
      -webkit-appearance: none;

      &:-webkit-autofill {
        box-shadow: 0 0 0px 1000px $loginBg inset !important;
        -webkit-text-fill-color: $lightGray!important;
      }
    }
  }

  .el-form-item {
    border-bottom: 1px solid #FFFFFF;
    background: rgba(0, 0, 0, 0);
    color: #454545;
  }
  .login-form-Code{
    display: flex;
    justify-content:space-between;
    .el-form-item {
      width: 200px;
    }
    .verifi-code-img{
      width: 120px;
      height: 50px;
    }
  }
  .el-checkbox{
    padding-left:5px;
    margin-bottom: 20px;
    .el-checkbox__inner{
      background: transparent;
    }
    .el-checkbox__label{
      color:$lightGray;
    }
  }
}
</style>

<style lang="scss" scoped>
.login-container {
  height: 100vh;
  width: 100%;
  overflow: hidden;
 .image{
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    width:100%;
    height:100%;
    object-fit: cover;
    z-index: -1;
 }
  // video {
  //   position: absolute;
  //   top: 0; left: 0; right: 0; bottom: 0;
  //   width:100%;
  //   height:100%;
  //   object-fit: cover;
  //   z-index: -1;
  // }
  .login-form {
    position: relative;
    width: 360px;
    max-width: 100%;
    margin: 0 auto;
    overflow: hidden;
  }

  .svg-container {
    padding: 6px 5px 6px 5px;
    color: #fff;
    vertical-align: middle;
    width: 30px;
    display: inline-block;
    i{
      font-size:20px;
    }
  }

  .title-container {
    position: relative;

    .title {
      font-size: 26px;
      color: #fff;
      margin: 0px auto 20px auto;
      text-align: center;
      font-weight: bold;
    }
  }

  .show-pwd {
    position: absolute;
    right: 10px;
    top: 7px;
    font-size: 16px;
    color: $darkGray;
    cursor: pointer;
    user-select: none;
  }

  .thirdparty-button {
    position: absolute;
    right: 0;
    bottom: 6px;
  }

  @media only screen and (max-width: 470px) {
    .thirdparty-button {
      display: none;
    }
  }
}
</style>
